import { forwardRef } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

const cardVariants = tv({
  base: 'bg-surface border-outline-strong flex flex-col rounded-xl border',
  variants: {
    padding: {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8'
    },
    shadow: {
      flat: 'shadow-flat',
      overlay: 'shadow-overlay',
      none: 'shadow-none'
    }
  },
  defaultVariants: {
    padding: 'md',
    shadow: 'flat'
  }
})

export type CardPadding = NonNullable<
  VariantProps<typeof cardVariants>['padding']
>

export type CardShadow = NonNullable<
  VariantProps<typeof cardVariants>['shadow']
>

export type CardProps = React.ComponentPropsWithRef<'div'> &
  VariantProps<typeof cardVariants>

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ padding, shadow, className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card'
      data-padding={padding || 'md'}
      data-shadow={shadow || 'flat'}
      className={cn(cardVariants({ padding, shadow }), className)}
      {...props}
    />
  )
)
Card.displayName = 'Card'

/* ------------------------------------------------------------------ */
/*  CardHeader                                                        */
/* ------------------------------------------------------------------ */

type CardHeaderProps = React.ComponentPropsWithRef<'div'>

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card-header'
      className={cn(
        'has-data-[slot=card-action]:grid has-data-[slot=card-action]:auto-rows-min has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-action]:items-start flex flex-col gap-1.5',
        className
      )}
      {...props}
    />
  )
)
CardHeader.displayName = 'CardHeader'

/* ------------------------------------------------------------------ */
/*  CardTitle                                                         */
/* ------------------------------------------------------------------ */

type CardTitleProps = React.ComponentPropsWithRef<'div'>

export const CardTitle = forwardRef<HTMLDivElement, CardTitleProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card-title'
      className={cn(
        'text-surface-title-50 font-heading text-lg font-semibold',
        className
      )}
      {...props}
    />
  )
)
CardTitle.displayName = 'CardTitle'

/* ------------------------------------------------------------------ */
/*  CardDescription                                                   */
/* ------------------------------------------------------------------ */

type CardDescriptionProps = React.ComponentPropsWithRef<'div'>

export const CardDescription = forwardRef<HTMLDivElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card-description'
      className={cn('text-surface-body text-sm', className)}
      {...props}
    />
  )
)
CardDescription.displayName = 'CardDescription'

/* ------------------------------------------------------------------ */
/*  CardAction                                                        */
/* ------------------------------------------------------------------ */

type CardActionProps = React.ComponentPropsWithRef<'div'>

export const CardAction = forwardRef<HTMLDivElement, CardActionProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card-action'
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  )
)
CardAction.displayName = 'CardAction'

/* ------------------------------------------------------------------ */
/*  CardContent                                                       */
/* ------------------------------------------------------------------ */

type CardContentProps = React.ComponentPropsWithRef<'div'>

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card-content'
      className={cn(className)}
      {...props}
    />
  )
)
CardContent.displayName = 'CardContent'

/* ------------------------------------------------------------------ */
/*  CardFooter                                                        */
/* ------------------------------------------------------------------ */

type CardFooterProps = React.ComponentPropsWithRef<'div'>

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot='card-footer'
      className={cn('flex items-center gap-2', className)}
      {...props}
    />
  )
)
CardFooter.displayName = 'CardFooter'
